"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getPoolsData = getPoolsData;
exports.getPoolLoans = getPoolLoans;
exports.getPoolLoansData = getPoolLoansData;
const abis_1 = require("@augustdigital/abis");
const utils_1 = require("@augustdigital/utils");
const ethers_1 = require("ethers");
const functionsToCall = [
    'decimals',
    'symbol',
    'name',
    'asset',
    'totalSupply',
    'totalAssets',
    'globalLoansAmount',
    'loansOperator',
    'getTotalLoansDeployed',
];
function getPoolsData(pools, rpcUrl) {
    return __awaiter(this, void 0, void 0, function* () {
        const provider = (0, utils_1.createProvider)(rpcUrl);
        try {
            const contracts = yield Promise.all(pools.map((pool) => {
                const contract = (0, utils_1.createContract)({
                    address: pool,
                    abi: abis_1.ABI_LENDING_POOLS,
                    provider,
                });
                return Promise.all(functionsToCall.map((f) => contract[f]()));
            }));
            const formatted = contracts
                .map((contract) => contract.reduce((acc, curr, i) => {
                let val;
                if (functionsToCall[i] === 'totalSupply' ||
                    functionsToCall[i] === 'totalAssets' ||
                    functionsToCall[i] === 'globalLoansAmount') {
                    val = (0, utils_1.toNormalizedBn)(curr, contract[0]);
                }
                else if (functionsToCall[i] === 'getTotalLoansDeployed') {
                    val = (0, utils_1.toNormalizedBn)(curr, 0);
                }
                else if (functionsToCall[i] === 'decimals') {
                    val = Number(curr.toString());
                }
                else if (functionsToCall[i] === 'loansOperator') {
                    val = '0x4DCb388488622e47683EAd1a147947140a31e485';
                }
                else {
                    val = curr;
                }
                return Object.assign(Object.assign({}, acc), { [functionsToCall[i]]: val });
            }, {}))
                .reduce((acc, curr, i) => {
                return Object.assign(Object.assign({}, acc), { [pools[i]]: curr });
            }, {});
            const withUnderlyingAsset = yield Promise.all(Object.keys(formatted).map((key) => __awaiter(this, void 0, void 0, function* () {
                const [symbol, decimals] = yield (0, utils_1.getTokenMetadata)(provider, formatted[key].asset, ['symbol', 'decimals']);
                return Object.assign(Object.assign({}, formatted[key]), { address: key, underlying: {
                        symbol: symbol,
                        decimals: Number(decimals.toString()),
                        address: formatted[key].asset,
                    } });
            })));
            return withUnderlyingAsset;
        }
        catch (e) {
            console.error('#getPoolsData:', e);
        }
    });
}
function getPoolLoans(pool, rpcUrl) {
    return __awaiter(this, void 0, void 0, function* () {
        const provider = (0, utils_1.createProvider)(rpcUrl);
        let loans = [];
        if (pool.getTotalLoansDeployed.raw > BigInt(0)) {
            loans = yield Promise.all(Array(pool.getTotalLoansDeployed.raw).map((i) => {
                const poolContract = (0, utils_1.createContract)({
                    address: pool.address,
                    abi: abis_1.ABI_LENDING_POOLS,
                    provider,
                });
                const loansDeployed = poolContract.loansDeployed(i - BigInt(i));
                return loansDeployed;
            }));
        }
        return loans;
    });
}
function getPoolLoansData(pool, loans, rpcUrl) {
    return __awaiter(this, void 0, void 0, function* () {
        const provider = (0, utils_1.createProvider)(rpcUrl);
        const loansAprCalcPrep = [];
        let aggregateApr = BigInt(0);
        let aggregateBorrowed = BigInt(0);
        let totalBorrowed = BigInt(0);
        const collateralTokens = [];
        let decimals = 18;
        let loansWithData = [];
        yield Promise.all(loans === null || loans === void 0 ? void 0 : loans.map((loan) => __awaiter(this, void 0, void 0, function* () {
            const loanContract = (0, utils_1.createContract)({
                provider,
                address: loan,
                abi: abis_1.ABI_LOAN,
            });
            const [_loanApr, _principalAmt, _collateral] = yield Promise.all([
                loanContract.currentApr(),
                loanContract.principalAmount(),
                loanContract.collateralToken(),
            ]);
            let _decimals = 18;
            if (_collateral !== ethers_1.ZeroAddress) {
                const [collateralDecimals] = yield (0, utils_1.getTokenMetadata)(provider, _collateral, ['decimals']);
                _decimals = collateralDecimals;
            }
            loansAprCalcPrep.push({
                apr: _loanApr,
                borrowed: _principalAmt,
                weight: BigInt(0),
            });
            aggregateApr += _loanApr;
            aggregateBorrowed += _principalAmt;
            totalBorrowed += _principalAmt;
            collateralTokens.push(_collateral);
            if (_collateral !== ethers_1.ZeroAddress)
                decimals = Number(_decimals);
            loansWithData.push({
                address: loan,
                apr: _loanApr,
                collateral: _collateral,
                principal: _principalAmt,
                principleDecimals: _decimals,
                allocation: 0,
            });
        })));
        const test = loansAprCalcPrep === null || loansAprCalcPrep === void 0 ? void 0 : loansAprCalcPrep.map((loanPrep) => (Object.assign(Object.assign({}, loanPrep), { weight: (loanPrep.borrowed / aggregateBorrowed) * BigInt(100) })));
        const returnsObj = {
            apy: (loans === null || loans === void 0 ? void 0 : loans.length)
                ? (0, utils_1.toNormalizedBn)(aggregateApr / BigInt((loans === null || loans === void 0 ? void 0 : loans.length) || 0), 2)
                : (0, utils_1.toNormalizedBn)(0, 2),
            totalBorrowed: (0, utils_1.toNormalizedBn)(totalBorrowed, decimals),
            collateral: collateralTokens,
            loans: loansWithData.map((l) => (Object.assign(Object.assign({}, l), { allocation: Number(l.principal / pool.totalSupply.raw) }))),
        };
        console.log('#getPoolLoansData:', returnsObj);
        return returnsObj;
    });
}
//# sourceMappingURL=helpers.js.map